import React, { useEffect, useMemo, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { Network } from '@capacitor/network';
import { useLocation, useNavigate } from 'react-router-dom';

const APP_LINK_HOSTS = new Set(['localally.in', 'www.localally.in']);

const isSensitiveExitPath = (pathname: string) =>
  /^\/(exam|scholarship\/exam|student\/exams|subscription\/payment|student\/wallet\/topup)/.test(pathname);

const normalizeAppLinkPath = (urlValue: string) => {
  try {
    const parsed = new URL(urlValue);
    if (!APP_LINK_HOSTS.has(parsed.hostname)) return null;

    const examMatch = parsed.pathname.match(/^\/exams\/([^/]+)$/);
    if (examMatch) {
      return `/exam/${encodeURIComponent(examMatch[1])}${parsed.search}${parsed.hash}`;
    }

    const tutorMatch = parsed.pathname.match(/^\/tutors\/([^/]+)$/);
    if (tutorMatch) {
      return `/student/teachers/${encodeURIComponent(tutorMatch[1])}/select${parsed.search}${parsed.hash}`;
    }

    const bookMatch = parsed.pathname.match(/^\/books\/([^/]+)$/);
    if (bookMatch) {
      return `/books/listings/${encodeURIComponent(bookMatch[1])}${parsed.search}${parsed.hash}`;
    }

    return `${parsed.pathname || '/'}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
};

const MobileAppBridge: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOffline, setIsOffline] = useState(false);
  const isNative = useMemo(() => Capacitor.isNativePlatform(), []);

  useEffect(() => {
    if (!isNative) return undefined;

    let disposed = false;
    let removeUrlListener: (() => void) | undefined;
    let removeBackListener: (() => void) | undefined;

    const attachListeners = async () => {
      const urlListener = await CapacitorApp.addListener('appUrlOpen', (event) => {
        const targetPath = normalizeAppLinkPath(event.url);
        if (targetPath) {
          navigate(targetPath);
        }
      });

      const backListener = await CapacitorApp.addListener('backButton', ({ canGoBack }) => {
        if (isSensitiveExitPath(window.location.pathname)) {
          const shouldLeave = window.confirm('Leave this flow? Unsaved progress or pending payment status may be lost.');
          if (!shouldLeave) return;
        }

        if (canGoBack || window.history.length > 1) {
          navigate(-1);
          return;
        }

        const shouldExit = window.confirm('Exit LocalAlly?');
        if (shouldExit) {
          void CapacitorApp.exitApp();
        }
      });

      if (disposed) {
        void urlListener.remove();
        void backListener.remove();
        return;
      }

      removeUrlListener = () => {
        void urlListener.remove();
      };
      removeBackListener = () => {
        void backListener.remove();
      };
    };

    void attachListeners();

    return () => {
      disposed = true;
      removeUrlListener?.();
      removeBackListener?.();
    };
  }, [isNative, navigate]);

  useEffect(() => {
    let disposed = false;
    let removeNetworkListener: (() => void) | undefined;

    const attachNativeNetwork = async () => {
      const status = await Network.getStatus();
      if (!disposed) setIsOffline(!status.connected);

      const listener = await Network.addListener('networkStatusChange', (nextStatus) => {
        setIsOffline(!nextStatus.connected);
      });

      if (disposed) {
        void listener.remove();
        return;
      }

      removeNetworkListener = () => {
        void listener.remove();
      };
    };

    const syncBrowserNetwork = () => setIsOffline(!navigator.onLine);

    if (isNative) {
      void attachNativeNetwork();
    } else {
      syncBrowserNetwork();
      window.addEventListener('online', syncBrowserNetwork);
      window.addEventListener('offline', syncBrowserNetwork);
    }

    return () => {
      disposed = true;
      removeNetworkListener?.();
      window.removeEventListener('online', syncBrowserNetwork);
      window.removeEventListener('offline', syncBrowserNetwork);
    };
  }, [isNative]);

  useEffect(() => {
    if (!isOffline) return;
    const title = document.title;
    document.title = `Offline - ${title.replace(/^Offline - /, '')}`;
    return () => {
      document.title = title;
    };
  }, [isOffline, location.pathname]);

  if (!isOffline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 z-[1600] mx-auto max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-900 shadow-xl shadow-slate-950/10"
    >
      You are offline. Saved screens remain visible; reconnect to load fresh tutors, exams, payments, and notifications.
    </div>
  );
};

export default MobileAppBridge;
